import { DATE_FORMAT_DISPLAY, DATE_TIME_FORMAT_DISPLAY, DATE_TIME_FORMAT_ISO } from "../../constants";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import 'dayjs/locale/es';
import moment from "moment";
// @ts-ignore — moment locale imports lack type declarations
import 'moment/locale/es';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');
moment.locale('es');
const CR_TIMEZONE = "America/Costa_Rica";

/**
 * Calculates the duration between two times given as strings in "HH:mm" format.
 * 
 * @param startTime - The start time in "HH:mm" format.
 * @param endTime - The end time in "HH:mm" format.
 * @returns A string representing the duration in "Xh Ym" format, or "0h 0m" if the end time is earlier than or equal to the start time.
 */
export function calculateDuration(startTime: string, endTime: string): string {
    if (!startTime || !endTime) return "";

    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);

    const startDate = DateNowCr();
    startDate.setHours(startHours, startMinutes, 0, 0);

    const endDate = DateNowCr();
    endDate.setHours(endHours, endMinutes, 0, 0);

    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs <= 0) return "0h 0m";

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return `${hours}h ${minutes}m`;
}

/**
 * Calculates the duration between two Date objects and returns it in a human-readable format.
 * 
 * @param startDate - The starting Date.
 * @param endDate - The ending Date.
 * @returns A string representing the duration in "Xh Ym" format, "Xs" for seconds, or "Expired" if the end date is in the past.
 */
export function calculateDurationBetweenDates(startDate: string, endDate: string | null): string {
    if (!endDate || startDate >= endDate) return "Fecha Expirada.";

    const diffMs = new Date(endDate).getTime() - new Date(startDate).getTime();

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    } else {
        return `${seconds}s`;
    }
}

/**
 * 
 * Validates if an endTime in "HH:mm" format is after the startTime in "HH:mm" format.
 * 
 * @param endTime - The end time in "HH:mm" format.
 * @param startTime - The start time in "HH:mm" format.
 * @returns True when endTime > startTime
 */
export function validateEndTimeAfterStartTime(endTime: string, startTime: string): boolean {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const startDate = DateNowCr();
    startDate.setHours(startHours, startMinutes);

    const endDate = DateNowCr();
    endDate.setHours(endHours, endMinutes);

    return (endDate > startDate);
};

/**
 * Calculates the duration between two times given as strings in "HH:mm" format.
 * 
 * @param startTime - The start time in "HH:mm" format.
 * @param endTime - The end time in "HH:mm" format.
 * @returns A string representing the duration in "Xh Ym" format, or "0h 0m" if the end time is earlier than or equal to the start time.
 */
export function getTimePlusMinutes(time: string, minutesToAdd: number): string {
    if (!time) return "";

    const [hours, minutes] = time.split(':').map(Number);
    const newEndTime = DateNowCr();
    newEndTime.setHours(hours, minutes + minutesToAdd);

    return newEndTime.toTimeString().slice(0, 5); // Format HH:mm
}

export function convertUTCDateToCostaRicaDate(date: Date) {
    if (!date) {
        throw new Error("Invalid date: date is null or undefined");
    }

    return dayjs.utc(date).tz(CR_TIMEZONE);
}

export function convertDayJsToCostaRicaDate(date: Dayjs) {
    if (!date) {
        throw new Error("Invalid date: date is null or undefined");
    }

    return date.tz(CR_TIMEZONE);
}

export function convertDayJsToCostaRicaDateIsoString(date: Dayjs) {
    return FormatDayJsToISOString(convertDayJsToCostaRicaDate(date));
}

export function getUTCDateAsLocal(date: Date) {
    if (!date) {
        throw new Error("Invalid date: date is null or undefined");
    }

    return dayjs.utc(date);
}

/**
 * Formats a date for user display
 * 
 * @param date - either a Date or a String.
 * @returns A string representing the date un user format
 */
export function formatDateForUserDisplay(date: any): string {
    if (!date) return "";

    return getUTCDateAsLocal(date).format(DATE_FORMAT_DISPLAY);
}

/**
 * Formats a date for user display
 * 
 * @param date - either a Date or a String.
 * @returns A string representing the date un user format
 */
export function formatDateTimeForUserDisplay(date: any): string {
    if (!date) return "";

    return convertUTCDateToCostaRicaDate(date).format(DATE_TIME_FORMAT_DISPLAY);
}

/**
 * Formats a date for user display in a detailed Spanish format
 * e.g., "1 de Noviembre - 2025"
 * 
 * @param date - either a Date or a String.
 * @returns A string representing the date in detailed user format.
 */
export function formatDateForDetailedUserDisplay(date: any): string {
    if (!date) return "";

    const parsedDate = convertUTCDateToCostaRicaDate(date);
    if (!parsedDate.isValid()) return "";

    return `${parsedDate.date()} de ${parsedDate.format('MMMM')} del ${parsedDate.format('YYYY')}`;
}

/**
 * Formats a date string or Date object as "1 de Noviembre del 2025",
 * keeping the original date without timezone conversion.
 * 
 * @param date - either a Date object or a date string (e.g., "2025-11-01T00:00:00")
 * @returns A string representing the date in detailed user format.
 */
export function formatDateForDetailedUserDisplayWithoutTZ(date: string | Date | undefined): string {
    if (!date) return "";

    const parsed = moment.parseZone(date);
    if (!parsed.isValid()) return "";
  
    return `${parsed.date()} de ${parsed.format('MMMM')} del ${parsed.year()}`;
  }

/**
 * Formats a date for user display in a detailed Spanish format with time
 * e.g., "1 de Noviembre - 2025 a las 7:00am"
 * 
 * @param date - either a Date or a String.
 * @returns A string representing the date in detailed user format with time.
 */
export function formatDateForDetailedUserDisplayWithTime(date: any): string {
    if (!date) return "";

    const parsedDate = convertUTCDateToCostaRicaDate(date);
    if (!parsedDate.isValid()) return "";

    const detailedDate = formatDateForDetailedUserDisplay(date);
    const time = parsedDate.format('h:mmA').toLowerCase(); // Format time as "7:00am" or "3:30pm"

    return `${detailedDate} a las ${time}`;
}
  
/**
 * Converts any date (string or Date) to a Dayjs object in Costa Rica time.
 */
export const FormatDayJsToISOString = (date: Dayjs): string => {
    return date.format(DATE_TIME_FORMAT_ISO);
};

/**
 * Converts a Dayjs date to a Costa Rica timezone ISO string.
 */
export const toCostaRicaISOString = (date: Dayjs): string => {
    return FormatDayJsToISOString(date.tz(CR_TIMEZONE));
  };
  
  /**
   * Converts any date (string or Date) to a Dayjs object in Costa Rica time.
   */
  export const parseToCRDayjs = (date: string | Date): Dayjs => {
    return dayjs.utc(date).tz(CR_TIMEZONE);
  };
  
/**
 * Converts any date (string or Date) to a Dayjs object in Costa Rica time.
 */
export const DateNowCr = (): Date => {
  return dayjs(new Date()).tz(CR_TIMEZONE).toDate();
};
  
/**
 * Converts any date (string or Date) to a Dayjs object in Costa Rica time.
 */
export const DateNowCrISOString = (): string => {
  return dayjs(DateNowCr()).format(DATE_TIME_FORMAT_ISO);
};

export function getCurrentDateForFilenameCr(): string {
    return dayjs(new Date()).tz(CR_TIMEZONE).format("YYYY_MM_DD");
  }
  
function addMinutes(date: Date, minutes: number): Date {
    const result = new Date(date);
    result.setMinutes(result.getMinutes() + minutes);
    return result;
}

export const getDateNowPlusMinutes = (minutes: number) : Date => {
    const nowDatePlusHours = new Date();

    return addMinutes(nowDatePlusHours, minutes);
}

export const minutesFromNowISO = (m: number) =>
    FormatDayJsToISOString(parseToCRDayjs(getDateNowPlusMinutes(m)));

export const DAYS_OF_WEEK = [
    { value: 0, label: "Domingo" },
    { value: 1, label: "Lunes" },
    { value: 2, label: "Martes" },
    { value: 3, label: "Miércoles" },
    { value: 4, label: "Jueves" },
    { value: 5, label: "Viernes" },
    { value: 6, label: "Sábado" },
  ];

export const HOURS_OF_DAY = [
    { value: "00", label: "12:00 AM" },
    { value: "01", label: "1:00 AM" },
    { value: "02", label: "2:00 AM" },
    { value: "03", label: "3:00 AM" },
    { value: "04", label: "4:00 AM" },
    { value: "05", label: "5:00 AM" },
    { value: "06", label: "6:00 AM" },
    { value: "07", label: "7:00 AM" },
    { value: "08", label: "8:00 AM" },
    { value: "09", label: "9:00 AM" },
    { value: "10", label: "10:00 AM" },
    { value: "11", label: "11:00 AM" },
    { value: "12", label: "12:00 PM" },
    { value: "13", label: "1:00 PM" },
    { value: "14", label: "2:00 PM" },
    { value: "15", label: "3:00 PM" },
    { value: "16", label: "4:00 PM" },
    { value: "17", label: "5:00 PM" },
    { value: "18", label: "6:00 PM" },
    { value: "19", label: "7:00 PM" },
    { value: "20", label: "8:00 PM" },
    { value: "21", label: "9:00 PM" },
    { value: "22", label: "10:00 PM" },
    { value: "23", label: "11:00 PM" },
  ];

/** Formats a Date as "YYYY-MM-DD" in local time (avoids UTC-conversion day-shift). */
export const toLocalDateStr = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/** Time options formatted for HH:MM:SS select inputs (e.g. "09:00:00"). */
export const HOURS_OPTIONS = HOURS_OF_DAY.map(h => ({ value: `${h.value}:00:00`, label: h.label }));

/**
 * Returns the number of minutes from the current time (system clock) to a future end date.
 * Designed to work with ISO strings that include timezone offsets.
 * Rounds up to the next full minute. Returns 0 if end is invalid or in the past.
 */
export function getMinutesUntil(end: string | Date | null | undefined): number {
    if (!end) return 0;
    try {
        // Use current instant rather than a specific timezone; ISO strings carry their own offset
        const now = new Date();
        const endDate = typeof end === "string" ? new Date(end) : end;
        const msLeft = endDate.getTime() - now.getTime();
        if (!isFinite(msLeft) || msLeft <= 0) return 0;
        return Math.ceil(msLeft / 60000);
    } catch {
        return 0;
    }
}